import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, skip, takeUntil } from 'rxjs/operators';
import { forkJoin, of, Subject, Subscription } from 'rxjs';

import { DaterangePickerComponent } from '../../components/date-range-picker/date-range-picker.component';
import { TimezoneComponent } from '../../components/timezone/timezone.component';
import { FilterSectionComponent } from '../../components/filter-section/filter-section.component';
import { TrendsService } from '../../services/trends.service';
import { FilterService } from '../../services/filter.service';
import { SharedDataService } from '../../services/shared-data.service';
import { CommonService } from '../../services/common.service';
import { transformColumnPreferences, transformTestToFilters } from '../../shared/appUtil';
import * as Types from '../../shared/types';

import dayjs from 'dayjs';

/** Body class: undo global NOVA daterangepicker positioning while Trends is open. */
export const TRENDS_DASHBOARD_BODY_CLASS = 'trends-dashboard-active';

/** Keep ngx body-appended picker clear of the left nav (picker width ~800px). */
export const TRENDS_DATERANGE_MIN_VIEWPORT_LEFT_PX = 88;
export const TRENDS_DATERANGE_VIEWPORT_MARGIN_PX = 16;

/** Adjust ngx's inline position so the popup stays inside the viewport (Trends only). */
export function clampTrendsDatepickerToViewport(
  picker: HTMLElement,
  minLeft = TRENDS_DATERANGE_MIN_VIEWPORT_LEFT_PX,
  margin = TRENDS_DATERANGE_VIEWPORT_MARGIN_PX
): void {
  const rect = picker.getBoundingClientRect();
  let deltaX = 0;
  if (rect.left < minLeft) {
    deltaX = minLeft - rect.left;
  }
  const maxRight = window.innerWidth - margin;
  if (rect.right + deltaX > maxRight) {
    deltaX -= rect.right + deltaX - maxRight;
  }

  let deltaY = 0;
  if (rect.top < margin) {
    deltaY = margin - rect.top;
  }
  const maxBottom = window.innerHeight - margin;
  if (rect.bottom + deltaY > maxBottom) {
    const flipAbove = -(rect.height + 48);
    deltaY = rect.top + flipAbove >= margin ? flipAbove : deltaY - (rect.bottom + deltaY - maxBottom);
  }

  if (deltaX !== 0) {
    const leftPx = parseFloat(picker.style.left);
    if (!Number.isNaN(leftPx)) {
      picker.style.left = `${leftPx + deltaX}px`;
      picker.style.right = 'auto';
    } else {
      const existing = picker.style.transform?.match(/translateX\(([-\d.]+)px\)/);
      const prior = existing ? parseFloat(existing[1]) : 0;
      picker.style.transform = `translateX(${prior + deltaX}px)`;
    }
  }

  if (deltaY !== 0) {
    const topPx = parseFloat(picker.style.top);
    if (!Number.isNaN(topPx)) {
      picker.style.top = `${topPx + deltaY}px`;
      picker.style.bottom = 'auto';
    }
  }
}

const TRENDS_DATERANGE_SESSION_KEY = 'trends-daterange-session';
const TRENDS_DATERANGE_TTL_MS = 30 * 60 * 1000; // 30 minutes
/** Backend rejects any single window longer than this (see trends_app TRENDS_MAX_RANGE_DAYS). */
export const TRENDS_MAX_RANGE_DAYS = 366;

/** Single key metric value from API (base vs comparison + deviation) */
export interface KeyMetricValue {
  base_value: number;
  comparison_value: number;
  deviation_percentage: number;
  deviation_type: 'rise' | 'fall' | 'no_change';
}

export interface TrendsParams {
  baseStartDate: string;
  baseEndDate: string;
  comparisonStartDate: string;
  comparisonEndDate: string;
  timezone: string;
  filters: Types.FilterRequest[];
}

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

/** Per-section load state: each telephony group / insights column loads independently. */
export interface SectionState<T> {
  status: LoadStatus;
  data: T | null;
  error: string;
}

export type InsightMetric = KeyMetricValue;
export type InsightRows = Record<string, InsightMetric>;

export type InsightChange = 'rise' | 'fall' | 'no_change';

export interface InsightRowVm {
  name: string;
  base: number;
  comparison: number;
  delta: number;
  percentage: number;
  change: InsightChange;
}


export interface InsightGroup {
  key: string;
  label: string;
  columns: string[];
}

export interface MetricCardDef {
  key: string;
  label: string;
  group: 'totals' | 'averages';
  icon: string;
  isDuration: boolean;
}

@Component({
  selector: 'app-trends-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DaterangePickerComponent,
    FilterSectionComponent,
    TimezoneComponent,
  ],
  providers: [
    TrendsService,
    FilterService,
    SharedDataService,
    CommonService
  ],
  templateUrl: './trends-dashboard.component.html',
  styleUrl: './trends-dashboard.component.scss',
})
export class TrendsDashboardComponent implements OnInit, AfterViewChecked, OnDestroy {
  private timezoneChangeSub?: Subscription;
  /** Emits to cancel every in-flight request (dates / filters / timezone / manual refresh changed). */
  private cancel$ = new Subject<void>();

  constructor(
    private trendsService: TrendsService,
    private filterService: FilterService,
    private sharedDataService: SharedDataService,
    private commonService: CommonService
  ) {}

  // Base period date range
  baseStartDate: string = '';
  baseEndDate: string = '';

  // Comparison period date range
  comparisonStartDate: string = '';
  comparisonEndDate: string = '';

  // UI state
  activeView: 'keyMetrics' | 'insightsMetrics' = 'keyMetrics';
  activeInsightGroup: string = 'topics';
  private rowCache = new Map<string, { src: InsightRows; rows: InsightRowVm[] }>();
  columnLoading: boolean = false;
  rangeError: string = '';
  /** True when dates / filters / timezone were edited after the last "Generate Trends" (no API call until clicked). */
  hasPendingChanges = false;
  /** Parameters of the last generated report; all API calls and column headers use this snapshot, never live edits. */
  generated: TrendsParams | null = null;

  // Per-section load states (each backed by its own API call, fired in parallel)
  totalsState: SectionState<Record<string, KeyMetricValue>> = this.emptyState();
  averagesState: SectionState<Record<string, KeyMetricValue>> = this.emptyState();
  columnStates: Record<string, SectionState<InsightRows>> = {};

  /** Supported insights columns for API (request) and display labels */
  readonly INSIGHTS_COLUMN_LABELS: Record<string, string> = {
    primarytopic: 'Primary Topic',
    intent: 'Intent',
    rootcauseclustered: 'Root Cause (Clustered)',
    triggerclustered: 'Trigger (Clustered)',
    customerpainpointclustered: 'Pain Point (Clustered)',
    topicsclustered: 'Topics (Clustered)',
    customersentimentatstart: 'Customer Sentiment at Start',
    customersentimentatend: 'Customer Sentiment at End',
    outcomeclustered: 'Outcome (Clustered)',
    callertype: 'Caller Type',
    journeymoment: 'Journey Moment',
  };

  readonly INSIGHTS_COLUMNS_API_KEYS = Object.keys(this.INSIGHTS_COLUMN_LABELS);

  /** Sub-tabs of the Insights view; the columns of the active group load in parallel, other groups lazily. */
  readonly INSIGHT_GROUPS: InsightGroup[] = [
    { key: 'topics', label: 'Primary Topic & Intent', columns: ['primarytopic', 'intent'] },
    { key: 'rootcauseclustered', label: 'Root Cause (Clustered)', columns: ['rootcauseclustered'] },
    { key: 'triggerclustered', label: 'Trigger (Clustered)', columns: ['triggerclustered'] },
    { key: 'customerpainpointclustered', label: 'Pain Point (Clustered)', columns: ['customerpainpointclustered'] },
    { key: 'topicsclustered', label: 'Topics (Clustered)', columns: ['topicsclustered'] },
    { key: 'outcomeclustered', label: 'Outcome (Clustered)', columns: ['outcomeclustered'] },
    {
      key: 'sentiment',
      label: 'Sentiment at Start & End',
      columns: ['customersentimentatstart', 'customersentimentatend'],
    },
    { key: 'caller', label: 'Caller Type & Journey Moment', columns: ['callertype', 'journeymoment'] },
  ];

  /** Telephony cards: `group` decides which API call feeds the card. */
  readonly METRIC_CARDS: MetricCardDef[] = [
    { key: 'total_conversations', label: 'Total Conversations', group: 'totals', icon: 'forum', isDuration: false },
    { key: 'average_handle_time', label: 'Average Handle Time', group: 'averages', icon: 'timer', isDuration: true },
    { key: 'agent_to_agent_interaction_duration', label: 'Average Agent to Agent Interaction Duration', group: 'averages', icon: 'group', isDuration: true },
    { key: 'customer_hold_duration', label: 'Average Customer Hold Duration', group: 'averages', icon: 'pause', isDuration: true },
    { key: 'queue_duration', label: 'Average Queue Duration', group: 'averages', icon: 'hourglass_empty', isDuration: true },
  ];

  /** Runs after ngx-daterangepicker's own AfterViewChecked, which resets left/top. */
  ngAfterViewChecked(): void {
    this.clampOpenTrendsDatepicker();
  }

  ngOnInit() {
    document.body.classList.add(TRENDS_DASHBOARD_BODY_CLASS);
    this.initializeDefaultDateRanges();
    this.applyStoredDateRangeIfValid();
    this.loadColumnNames();
    this.loadFilterData();
    this.generateTrends(); // one automatic load on open; afterwards only the Generate Trends button calls the APIs
    this.timezoneChangeSub = this.sharedDataService
      .getTimezoneChange()
      .pipe(skip(1))
      .subscribe(() => this.markPending());
  }

  ngOnDestroy() {
    document.body.classList.remove(TRENDS_DASHBOARD_BODY_CLASS);
    this.timezoneChangeSub?.unsubscribe();
    this.cancel$.next();
    this.cancel$.complete();
  }

  private clampOpenTrendsDatepicker(): void {
    if (!document.body.classList.contains(TRENDS_DASHBOARD_BODY_CLASS)) {
      return;
    }
    document.querySelectorAll<HTMLElement>('.daterangepicker.shown').forEach((picker) => {
      clampTrendsDatepickerToViewport(picker);
    });
  }

  private emptyState<T>(): SectionState<T> {
    return { status: 'idle', data: null, error: '' };
  }

  private resetColumnStates(): void {
    this.columnStates = {};
    this.rowCache.clear();
    this.INSIGHTS_COLUMNS_API_KEYS.forEach((c) => (this.columnStates[c] = this.emptyState()));
  }

  loadColumnNames() {
    this.columnLoading = true;
    this.commonService.getUserColumnPreference({}).subscribe({
      next: (names: Array<Types.ColumnPreferenceType>) => {
        const transformedNames = transformColumnPreferences(names);
        this.sharedDataService.setColumnNames(transformedNames);
        this.columnLoading = false;
      },
      error: (error) => {
        console.error('Error loading column names:', error);
        this.columnLoading = false;
      }
    });
  }

  /** Loads the filter dropdown options (same three endpoints as the Insights dashboard). */
  loadFilterData() {
    const empty = { filters: {} };
    forkJoin({
      insights: this.commonService.fetchDataFiltersInsights({}).pipe(catchError(() => of(empty))),
      clusters: this.commonService.fetchDataFiltersClusters({}).pipe(catchError(() => of(empty))),
      connect: this.commonService.fetchDataFiltersConnectAttributes({}).pipe(catchError(() => of(empty))),
    }).subscribe((result: any) => {
      const merged = { filters: {} } as Types.DataFilters;
      [result.insights, result.clusters, result.connect].forEach((data: any) => {
        if (data?.filters && typeof data.filters === 'object') {
          Object.assign(merged.filters, data.filters);
        }
      });
      this.filterService.updateAllFilters(merged);
    });
  }

  /** Same filter payload shape as the Insights dashboard (PascalCase column names, e.g. PrimaryTopic). */
  private getFiltersPayload(): Types.FilterRequest[] {
    return transformTestToFilters(this.filterService.getFilterConditionDataValue() || []).data ?? [];
  }

  /** Apply Changes in the filter panel: same filters go to telephony + every insights column request. */
  onFiltersApplied() {
    this.filterService.applyTempFiltersToActual();
    if (this.canGenerate) {
      this.generateTrends();
    } else {
      this.markPending();
    }
  }

  clearAllFilters() {
    this.filterService.clearFilters();
    this.filterService.applyTempFiltersToActual();
    if (this.canGenerate) {
      this.generateTrends();
    } else {
      this.markPending();
    }
  }

  initializeDefaultDateRanges() {
    const today = dayjs();

    // Base period: yesterday
    const yesterday = today.subtract(1, 'day');
    this.baseStartDate = yesterday.format('YYYY-MM-DD');
    this.baseEndDate = yesterday.format('YYYY-MM-DD');

    // Comparison period: day before yesterday
    const dayBeforeYesterday = today.subtract(2, 'day');
    this.comparisonStartDate = dayBeforeYesterday.format('YYYY-MM-DD');
    this.comparisonEndDate = dayBeforeYesterday.format('YYYY-MM-DD');
  }

  /** Returns default date range (base = yesterday, comparison = day before yesterday). */
  private getDefaultDateRange(): {
    baseStartDate: string;
    baseEndDate: string;
    comparisonStartDate: string;
    comparisonEndDate: string;
  } {
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');
    const dayBeforeYesterday = today.subtract(2, 'day');
    return {
      baseStartDate: yesterday.format('YYYY-MM-DD'),
      baseEndDate: yesterday.format('YYYY-MM-DD'),
      comparisonStartDate: dayBeforeYesterday.format('YYYY-MM-DD'),
      comparisonEndDate: dayBeforeYesterday.format('YYYY-MM-DD'),
    };
  }

  /** Load stored date range from session; apply only if not expired (< 30 min). Clear if expired. */
  private applyStoredDateRangeIfValid(): void {
    try {
      const raw = sessionStorage.getItem(TRENDS_DATERANGE_SESSION_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw) as {
        baseStartDate: string;
        baseEndDate: string;
        comparisonStartDate: string;
        comparisonEndDate: string;
        storedAt: number;
      };
      if (
        !stored.baseStartDate ||
        !stored.baseEndDate ||
        !stored.comparisonStartDate ||
        !stored.comparisonEndDate ||
        typeof stored.storedAt !== 'number'
      ) {
        sessionStorage.removeItem(TRENDS_DATERANGE_SESSION_KEY);
        return;
      }
      const now = Date.now();
      if (now - stored.storedAt > TRENDS_DATERANGE_TTL_MS) {
        sessionStorage.removeItem(TRENDS_DATERANGE_SESSION_KEY);
        return;
      }
      this.baseStartDate = stored.baseStartDate;
      this.baseEndDate = stored.baseEndDate;
      this.comparisonStartDate = stored.comparisonStartDate;
      this.comparisonEndDate = stored.comparisonEndDate;
    } catch {
      sessionStorage.removeItem(TRENDS_DATERANGE_SESSION_KEY);
    }
  }

  /** Persist current date range to session only if different from default. Clear session if same as default. */
  private persistDateRangeToSessionIfNeeded(): void {
    const defaultRange = this.getDefaultDateRange();
    const isDefault =
      this.baseStartDate === defaultRange.baseStartDate &&
      this.baseEndDate === defaultRange.baseEndDate &&
      this.comparisonStartDate === defaultRange.comparisonStartDate &&
      this.comparisonEndDate === defaultRange.comparisonEndDate;
    try {
      if (isDefault) {
        sessionStorage.removeItem(TRENDS_DATERANGE_SESSION_KEY);
        return;
      }
      sessionStorage.setItem(
        TRENDS_DATERANGE_SESSION_KEY,
        JSON.stringify({
          baseStartDate: this.baseStartDate,
          baseEndDate: this.baseEndDate,
          comparisonStartDate: this.comparisonStartDate,
          comparisonEndDate: this.comparisonEndDate,
          storedAt: Date.now(),
        })
      );
    } catch (e) {
      console.warn('Could not persist trends date range to session', e);
    }
  }

  onBaseDateRangeSelect(event: any) {
    if (event && event.startDate && event.endDate) {
      this.baseStartDate = event.startDate.format('YYYY-MM-DD');
      this.baseEndDate = event.endDate.format('YYYY-MM-DD');
      this.persistDateRangeToSessionIfNeeded();
      this.markPending();
    }
  }

  onComparisonDateRangeSelect(event: any) {
    if (event && event.startDate && event.endDate) {
      this.comparisonStartDate = event.startDate.format('YYYY-MM-DD');
      this.comparisonEndDate = event.endDate.format('YYYY-MM-DD');
      this.persistDateRangeToSessionIfNeeded();
      this.markPending();
    }
  }

  /** An input changed: remember it, validate it, but do not call any API. */
  markPending() {
    this.hasPendingChanges = true;
    this.rangeError = this.validateRanges();
  }

  get canGenerate(): boolean {
    return !this.rangeError && !!(this.baseStartDate && this.baseEndDate && this.comparisonStartDate && this.comparisonEndDate);
  }

  /** "Generate Trends": snapshot the inputs, cancel in-flight calls, discard old data and load the active tab. */
  generateTrends() {
    this.rangeError = this.validateRanges();
    if (!this.canGenerate) return;
    this.cancel$.next();
    this.generated = {
      baseStartDate: this.baseStartDate,
      baseEndDate: this.baseEndDate,
      comparisonStartDate: this.comparisonStartDate,
      comparisonEndDate: this.comparisonEndDate,
      timezone: this.sharedDataService.selectedTimezoneValue || 'UTC',
      filters: this.getFiltersPayload(),
    };
    this.hasPendingChanges = false;
    this.totalsState = this.emptyState();
    this.averagesState = this.emptyState();
    this.resetColumnStates();
    this.loadActiveView();
  }

  private validateRanges(): string {
    const tooLong = (s: string, e: string) => dayjs(e).diff(dayjs(s), 'day') > TRENDS_MAX_RANGE_DAYS;
    if (tooLong(this.baseStartDate, this.baseEndDate) || tooLong(this.comparisonStartDate, this.comparisonEndDate)) {
      return `Each date range can span at most ${TRENDS_MAX_RANGE_DAYS} days. Please shorten the Base or Comparison range.`;
    }
    return '';
  }

  /** Fires only the requests the visible tab needs, skipping sections that are already loaded/loading. */
  private loadActiveView() {
    if (!this.generated) return;
    if (this.activeView === 'keyMetrics') {
      this.loadTotals();
      this.loadAverages();
    } else {
      this.getActiveGroup().columns.forEach((c) => this.loadColumn(c));
    }
  }

  private buildPayload() {
    const g = this.generated!;
    return {
      base_duration: { start_date: g.baseStartDate, end_date: g.baseEndDate },
      comparison_duration: { start_date: g.comparisonStartDate, end_date: g.comparisonEndDate },
      timezone: g.timezone,
      filters: g.filters,
    };
  }

  /** Human readable message from an HttpErrorResponse / timeout. */
  private errorMessage(error: any): string {
    if (error?.name === 'TimeoutError') return 'The request took too long. Try a shorter date range or add filters.';
    const detail = error?.error?.detail ?? error?.error?.message ?? error?.message;
    if (typeof detail === 'string' && detail) return detail;
    return 'Something went wrong while loading this data.';
  }

  loadTotals() {
    if (this.totalsState.status === 'loading' || this.totalsState.status === 'success') return;
    this.totalsState = { status: 'loading', data: null, error: '' };
    this.trendsService.getTotalConversations$(this.buildPayload()).pipe(takeUntil(this.cancel$)).subscribe({
      next: (res) => (this.totalsState = { status: 'success', data: res?.data?.total_conversations ? { total_conversations: res.data.total_conversations } : {}, error: '' }),
      error: (e) => (this.totalsState = { status: 'error', data: null, error: this.errorMessage(e) }),
    });
  }

  loadAverages() {
    if (this.averagesState.status === 'loading' || this.averagesState.status === 'success') return;
    this.averagesState = { status: 'loading', data: null, error: '' };
    this.trendsService.getAverageDurations$(this.buildPayload()).pipe(takeUntil(this.cancel$)).subscribe({
      next: (res) => (this.averagesState = { status: 'success', data: res?.data ?? {}, error: '' }),
      error: (e) => (this.averagesState = { status: 'error', data: null, error: this.errorMessage(e) }),
    });
  }

  loadColumn(column: string) {
    const current = this.columnStates[column];
    if (current && (current.status === 'loading' || current.status === 'success')) return;
    this.columnStates[column] = { status: 'loading', data: null, error: '' };
    const payload = { ...this.buildPayload(), insights_column: column };
    this.trendsService.getInsightsColumn$(payload).pipe(takeUntil(this.cancel$)).subscribe({
      next: (res) => {
        const rows = res?.data?.insights_metrics?.[column] ?? res?.insights_metrics?.[column] ?? {};
        this.columnStates[column] = { status: 'success', data: rows, error: '' };
      },
      error: (e) => (this.columnStates[column] = { status: 'error', data: null, error: this.errorMessage(e) }),
    });
  }

  /** Retry a single failed section without touching the others. */
  retrySection(kind: 'totals' | 'averages' | 'column', column?: string) {
    if (kind === 'totals') {
      this.totalsState = this.emptyState();
      this.loadTotals();
    } else if (kind === 'averages') {
      this.averagesState = this.emptyState();
      this.loadAverages();
    } else if (column) {
      this.columnStates[column] = this.emptyState();
      this.loadColumn(column);
    }
  }

  getActiveGroup(): InsightGroup {
    return this.INSIGHT_GROUPS.find((g) => g.key === this.activeInsightGroup) ?? this.INSIGHT_GROUPS[0];
  }

  setActiveInsightGroup(key: string) {
    this.activeInsightGroup = key;
    this.loadActiveView();
  }

  get isTelephonyLoading(): boolean {
    return this.totalsState.status === 'loading' || this.averagesState.status === 'loading';
  }

  get isAnyLoading(): boolean {
    return this.isTelephonyLoading || Object.values(this.columnStates).some((s) => s.status === 'loading');
  }

  getCardState(card: MetricCardDef): SectionState<Record<string, KeyMetricValue>> {
    return card.group === 'totals' ? this.totalsState : this.averagesState;
  }

  getCardMetric(card: MetricCardDef): KeyMetricValue | null {
    return this.getCardState(card).data?.[card.key] ?? null;
  }

  formatMetricValue(value: number): string {
    if (value == null || Number.isNaN(Number(value))) {
      return '—';
    }
    return Number(value).toLocaleString();
  }

  trackByCard(_index: number, card: MetricCardDef): string {
    return card.key;
  }

  trackByGroup(_index: number, group: InsightGroup): string {
    return group.key;
  }

  trackByColumn(_index: number, column: string): string {
    return column;
  }

  trackByInsightRow(_index: number, row: InsightRowVm): string {
    return row.name;
  }

  /** Format seconds as Xm:YYs (e.g. 320 -> "5m:20s", 1073 -> "17m:53s") */
  formatDurationSeconds(seconds: number): string {
    if (seconds == null || Number.isNaN(seconds)) return '--m:--s';
    const totalSeconds = Math.floor(Number(seconds));
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}m:${String(s).padStart(2, '0')}s`;
  }

  calculatePercentageChange(base: number, comparison: number): number {
    if (comparison === 0) return base > 0 ? 100 : 0;
    return ((base - comparison) / comparison) * 100;
  }

  getChangeIcon(change: number): string {
    return change >= 0 ? 'trending_up' : 'trending_down';
  }

  getChangeClass(change: number): string {
    return change >= 0 ? 'increase-change' : 'decrease-change';
  }

  setActiveView(view: 'keyMetrics' | 'insightsMetrics') {
    this.activeView = view;
    // Loads only what this tab still needs; data already loaded for the same filters/dates is kept.
    this.loadActiveView();
  }

  /** Display rows for a column, sorted by absolute change. Memoised per response so change detection never re-sorts. */
  getInsightsRows(columnKey: string): InsightRowVm[] {
    const src = this.columnStates[columnKey]?.data;
    if (!src) return [];
    const cached = this.rowCache.get(columnKey);
    if (cached && cached.src === src) return cached.rows;

    const rows: InsightRowVm[] = Object.entries(src).map(([name, m]) => ({
      name,
      base: m.base_value,
      comparison: m.comparison_value,
      delta: m.base_value - m.comparison_value,
      percentage: m.deviation_percentage,
      change: m.deviation_type,
    }));
    rows.sort(
      (a, b) =>
        Math.abs(b.percentage) - Math.abs(a.percentage) || b.base + b.comparison - (a.base + a.comparison)
    );
    this.rowCache.set(columnKey, { src, rows });
    return rows;
  }

  formatInsightValue(value: number): string {
    if (value == null || Number.isNaN(Number(value))) {
      return '—';
    }
    return Number(value).toLocaleString();
  }

  /** Format date range for column headers */
  formatDateRange(startDate: string, endDate: string): string {
    if (!startDate || !endDate) return '';
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return `${start.format('DD MMM')} - ${end.format('DD MMM')}`;
  }

  /** Get formatted base period label */
  getBasePeriodLabel(): string {
    return this.formatDateRange(this.generated?.baseStartDate ?? '', this.generated?.baseEndDate ?? '');
  }

  /** Get formatted comparison period label */
  getComparisonPeriodLabel(): string {
    return this.formatDateRange(this.generated?.comparisonStartDate ?? '', this.generated?.comparisonEndDate ?? '');
  }
}
